import { Form, Input, Select, Space, Typography } from 'antd';
import { Button } from '@insight-health-ai/roseate/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { SuperAdminScope } from 'src/components';
import { BackIconMobile } from 'src/icons';
import { usePageHeader } from 'src/hooks/misc';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { FlowBuilder } from './components/FlowBuilder';
import { DEFAULT_FLOW_BUILDER_VALUE } from './components/FlowBuilder.types';
import { MagicFlowVersionsCard } from './components/MagicFlowVersionsCard';
import { MagicFlowVersionsProvider } from './MagicFlowVersionsContext';
import type {
  FlowStep,
  FlowTrigger,
  NamespaceOption,
  NamespaceRecord,
  RecipeRecord,
} from './MagicFlowAdmin.types';

type RecipeFormValues = {
  name: string;
  description?: string;
  namespace_id: string;
};

type ParsedFlowJson = {
  steps: FlowStep[];
  triggers: FlowTrigger[];
  custom_variables: Record<string, unknown>;
};

const buildInitialJson = (
  steps?: FlowStep[],
  triggers?: FlowTrigger[],
  customVariables?: Record<string, unknown>
) =>
  JSON.stringify(
    {
      steps: steps ?? [],
      triggers: triggers ?? [],
      custom_variables: customVariables ?? {},
    },
    null,
    2
  );

const DEFAULT_JSON = buildInitialJson(
  DEFAULT_FLOW_BUILDER_VALUE.steps,
  DEFAULT_FLOW_BUILDER_VALUE.triggers,
  DEFAULT_FLOW_BUILDER_VALUE.custom_variables
);

const parseFlowJson = (raw: string): ParsedFlowJson => {
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Top-level value must be an object');
  }
  return {
    steps: Array.isArray(parsed.steps) ? parsed.steps : [],
    triggers: Array.isArray(parsed.triggers) ? parsed.triggers : [],
    custom_variables:
      parsed.custom_variables && typeof parsed.custom_variables === 'object'
        ? parsed.custom_variables
        : {},
  };
};

const MagicFlowRecipeEditorContent = () => {
  const dispatch = useDispatch<any>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { recipeId } = useParams<{ recipeId?: string }>();
  const isEditMode = Boolean(recipeId && recipeId !== 'new');

  // Recipes are global: strip impersonation headers regardless of org selection.
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const [form] = Form.useForm<RecipeFormValues>();

  const [flowJson, setFlowJson] = useState(DEFAULT_JSON);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const recipeHeaders = useMemo(() => {
    const {
      'Impersonate-Org-UUID': _impersonatedOrg,
      'Headless-Mode': _headlessMode,
      ...rest
    } = (superAdminHeaders || {}) as Record<string, string | boolean>;
    return rest;
  }, [superAdminHeaders]);

  usePageHeader({
    title: isEditMode ? 'Edit Recipe' : 'Create Recipe',
  });

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  const { data: namespaceOptions = [] } = useQuery({
    queryKey: ['magicflow-namespaces'],
    enabled: true,
    queryFn: () =>
      magicFlowApi
        .get<{ namespaces?: NamespaceRecord[] }>('/namespace', {
          headers: recipeHeaders,
          params: {
            page: 1,
            page_size: 200,
            sort_by: 'updated_at',
            sort_direction: 'desc',
          },
        })
        .then(
          (res) =>
            (res.data?.namespaces || [])
              .filter((namespace) => namespace?.namespace_id && namespace?.name)
              .map((namespace) => ({
                value: String(namespace.namespace_id),
                label: String(namespace.name),
              })) as NamespaceOption[]
        ),
  });

  const { data: existingRecipe, isLoading: isLoadingRecipe } = useQuery({
    queryKey: ['magicflow-recipe-detail', recipeId],
    enabled: isEditMode,
    queryFn: () =>
      magicFlowApi
        .get<{ recipe: RecipeRecord }>(`/recipe/${recipeId}`, {
          headers: recipeHeaders,
        })
        .then((res) => res.data.recipe),
  });

  const { data: recipeVersions = [] } = useQuery({
    queryKey: [
      'magicflow-recipe-versions',
      existingRecipe?.key,
      existingRecipe?.namespace_id,
    ],
    enabled:
      Boolean(existingRecipe?.key) && Boolean(existingRecipe?.namespace_id),
    queryFn: () =>
      magicFlowApi
        .get<{
          versions: RecipeRecord[];
        }>(
          `/recipe/${existingRecipe!.key}/versions/${existingRecipe!.namespace_id}`,
          { headers: recipeHeaders }
        )
        .then((res) => res.data?.versions || []),
  });

  useEffect(() => {
    if (isEditMode) {
      if (!existingRecipe) return;
      form.setFieldsValue({
        name: existingRecipe.name,
        description: existingRecipe.description || '',
        namespace_id: existingRecipe.namespace_id,
      });
      setFlowJson(
        buildInitialJson(
          existingRecipe.steps,
          existingRecipe.triggers,
          existingRecipe.custom_variables
        )
      );
      setJsonError(null);
      return;
    }

    form.setFieldsValue({
      name: '',
      description: '',
      namespace_id: '',
    });
    setFlowJson(DEFAULT_JSON);
    setJsonError(null);
  }, [existingRecipe, form, isEditMode]);

  const handleJsonChange = (raw: string) => {
    setFlowJson(raw);
    try {
      parseFlowJson(raw);
      setJsonError(null);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const saveRecipeMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      namespace_id: string;
      steps: FlowStep[];
      triggers: FlowTrigger[];
      custom_variables: Record<string, unknown>;
    }) => {
      if (isEditMode) {
        return magicFlowApi.put(
          `/recipe/${recipeId}`,
          {
            name: payload.name,
            description: payload.description,
            steps: payload.steps,
            triggers: payload.triggers,
            custom_variables: payload.custom_variables,
          },
          { headers: recipeHeaders }
        );
      }
      return magicFlowApi.post('/recipe', payload, { headers: recipeHeaders });
    },
    onSuccess: () => {
      showToastSuccess(
        isEditMode
          ? 'Recipe updated successfully (new version created)'
          : 'Recipe created successfully'
      );
      queryClient.invalidateQueries({
        queryKey: ['magicflow-recipes-global'],
      });
      navigate('/superadmin/magicflow/recipes');
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to save recipe'
      );
    },
  });

  const activateVersionMutation = useMutation({
    mutationFn: (versionRecipeId: string) =>
      magicFlowApi.put(
        `/recipe/${versionRecipeId}/activate`,
        {},
        { headers: recipeHeaders }
      ),
    onSuccess: () => {
      showToastSuccess('Recipe version activated');
      queryClient.invalidateQueries({
        queryKey: [
          'magicflow-recipe-versions',
          existingRecipe?.key,
          existingRecipe?.namespace_id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['magicflow-recipes-global'],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to activate version'
      );
    },
  });

  const handleSave = async () => {
    let parsed: ParsedFlowJson;
    try {
      parsed = parseFlowJson(flowJson);
      setJsonError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON';
      setJsonError(message);
      showToastError(`Invalid JSON: ${message}`);
      return;
    }

    await form.validateFields();
    const values = form.getFieldsValue(true) as RecipeFormValues;
    saveRecipeMutation.mutate({
      name: values.name,
      description: values.description,
      namespace_id: values.namespace_id,
      steps: parsed.steps,
      triggers: parsed.triggers,
      custom_variables: parsed.custom_variables,
    });
  };

  const versionsForCard = useMemo(
    () =>
      recipeVersions.map((version) => ({
        id: version.recipe_id,
        version: version.version,
        is_active: version.is_active,
        updated_at: version.updated_at,
      })),
    [recipeVersions]
  );

  const topFormItemLayout = {
    labelCol: { flex: '140px' },
    wrapperCol: { flex: 'auto' },
  };

  return (
    <div className="page-wrapper">
      <div className="flex justify-between items-center gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BackIconMobile
            className=""
            onClick={() => navigate('/superadmin/magicflow/recipes')}
          />
          <Typography.Text className="font-medium">Recipes</Typography.Text>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-9">
          <Form form={form} layout="horizontal" labelAlign="left">
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Name is required' }]}
              {...topFormItemLayout}
            >
              <Input placeholder="Recipe name" />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              {...topFormItemLayout}
            >
              <Input.TextArea rows={2} placeholder="Description (optional)" />
            </Form.Item>
            <Form.Item
              label="Namespace"
              name="namespace_id"
              rules={[{ required: true, message: 'Namespace is required' }]}
              {...topFormItemLayout}
            >
              <Select
                showSearch
                allowClear
                options={namespaceOptions}
                placeholder="Select namespace"
                optionFilterProp="label"
                disabled={isEditMode}
              />
            </Form.Item>

            <FlowBuilder
              value={flowJson}
              onChange={handleJsonChange}
              error={jsonError}
            />
          </Form>
        </div>

        {isEditMode ? (
          <div className="xl:col-span-3">
            <MagicFlowVersionsProvider
              versions={versionsForCard}
              activatingId={activateVersionMutation.variables}
              isActivatingPending={activateVersionMutation.isPending}
              onActivate={(id) => activateVersionMutation.mutate(id)}
              onSelect={(id) => navigate(`/superadmin/magicflow/recipes/${id}`)}
              currentId={recipeId}
            >
              <MagicFlowVersionsCard />
            </MagicFlowVersionsProvider>
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex justify-end">
        <Space>
          <Button
            variant="outlined"
            appearance="secondary"
            onClick={() => navigate('/superadmin/magicflow/recipes')}
          >
            Cancel
          </Button>
          <Button
            appearance="primary"
            loading={saveRecipeMutation.isPending}
            onClick={handleSave}
            disabled={isLoadingRecipe}
          >
            {isEditMode ? 'Save (new version)' : 'Create Recipe'}
          </Button>
        </Space>
      </div>
    </div>
  );
};

const MagicFlowRecipeEditorPage = () => (
  <SuperAdminScope>
    <MagicFlowRecipeEditorContent />
  </SuperAdminScope>
);

export default MagicFlowRecipeEditorPage;
